import React from 'react';
import { BaseTimelineItemFormLayout } from 'framework/ui/timeline';
import useProjectNotesService from '../useProjectNotesService';
import ProjectNoteForm from './ProjectNoteForm';
import { DEFAULT_ERROR_MESSAGE, FormFieldsData, UpdateForm } from 'framework/form';
import { NotePayload, ProjectNote } from '../types';
import { cloneDeep } from 'lodash';
import { ErrorMessage, useMessageService } from 'framework/ui/Message';

interface ProjectNoteTimelineUpdateFormProps {
  messageContext: string;
  entityId?: string;
  afterSubmit?: { execute?: (id: StringOrNumber, data: FormFieldsData) => void };
  getDataFunc: () => Promise<ProjectNote>;
  onCancel: () => void;
}

const ProjectNoteTimelineUpdateForm = ({
  entityId,
  getDataFunc,
  onCancel,
  messageContext,
  ...rest
}: ProjectNoteTimelineUpdateFormProps) => {
  const { updateUserNote, getNoteValidationSchema } = useProjectNotesService();
  const { addMessage } = useMessageService(messageContext);

  const updateFormFunc = async (id: StringOrNumber, values: FormFieldsData): Promise<NotePayload> => {
    const formData = cloneDeep(values) as NotePayload;
    return await updateUserNote(id.toString(), {
      text: formData.text,
      attachmentPaths: formData.attachmentPaths || [],
    });
  };

  const getMappedDataFunc = async () => {
    const entity = await getDataFunc();
    const note = entity?.payload as { text?: string; attachmentPaths?: string[] } | undefined;
    return {
      text: note?.text ?? '',
      attachmentPaths: note?.attachmentPaths ?? [],
    };
  };

  const handleErrorsOnSubmit = (error: Response) => {
    if (error.status === 403) {
      addMessage(new ErrorMessage(`You don't have permission to update this note.`));
    } else {
      addMessage(new ErrorMessage(DEFAULT_ERROR_MESSAGE));
    }
  };

  if (!entityId) return null;

  return (
    <UpdateForm
      entityId={entityId}
      getDataFunc={getMappedDataFunc}
      submitFormFunc={updateFormFunc}
      getValidationSchemaFunc={getNoteValidationSchema}
      handleErrorsOnSubmit={handleErrorsOnSubmit}
      messageContext={messageContext}
      {...rest}
    >
      <BaseTimelineItemFormLayout onCancel={onCancel}>
        <ProjectNoteForm />
      </BaseTimelineItemFormLayout>
    </UpdateForm>
  );
};

export default ProjectNoteTimelineUpdateForm;
